/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { retailAction } from '@msdyn365-commerce/retail-proxy';

import { addToCartAsyncInput } from './DataActionExtension.g';
import { ILobasCartStagingHandoffConfig } from '../modules/lobas-cart-staging-handoff/lobas-cart-staging-handoff.props.autogenerated';

/**
 * GetCartStagingData Input Action
 */
export class GetCartStagingDataInput implements Msdyn365.IActionInput {
    public intentId: string;

    public cartId: string;

    public constructor(intentId: string, cartId: string) {
        this.intentId = intentId;
        this.cartId = cartId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `CartStagingHandoff:${this.intentId}:${this.cartId}`;
    public getCacheObjectType = () => 'CartStagingHandoff';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

const getIntentId = (ctx: Msdyn365.ICreateActionContext, paramName: string): string => {
    const raw = ctx.requestContext?.url?.requestUrl?.search || '';
    const sp = new URLSearchParams(raw.startsWith('?') ? raw : `?${raw}`);
    return (sp.get(paramName) || '').trim();
};

const getCartIdFromCookie = (ctx: Msdyn365.ICreateActionContext): string => {
    const cookies = ctx.requestContext.cookies;
    if (!cookies.isConsentGiven()) {
        throw new Error('Cookie consent not given');
    }

    const cartCookie = cookies.getCartCookie();
    if (!cartCookie) {
        throw new Error('No active cart cookie found');
    }

    return cartCookie.split(':').length > 1 ? cartCookie.split(':')[1] : cartCookie;
};

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext<ILobasCartStagingHandoffConfig>): Msdyn365.IActionInput => {
    const stagingParamName = (args.config?.intentQueryParam as string) || 'intentId';
    const intentId = getIntentId(args, stagingParamName);
    if (!intentId) {
        throw new Error(`Missing query param: ${stagingParamName}`);
    }

    const cartId = getCartIdFromCookie(args);

    return new GetCartStagingDataInput(intentId, cartId);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetCartStagingDataInput, ctx: Msdyn365.IActionContext): Promise<string> {
    return retailAction(addToCartAsyncInput(input.intentId, input.cartId), ctx);
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<string>>action,
    id: 'GetCartStagingData',
    input: createInput
});
